import { useCallback, useEffect, useRef, useState } from 'react';

import { StreamTypes } from 'store/player/actions.types';

import { VideoItem } from 'services/data-manager/types';
import { UavEvents } from 'services/uav/types';
import useGetBaseUavParams from 'services/uav/useGetBaseUavParams';
import useGetPlaybackStartUavParams from 'services/uav/useGetPlaybackStartUavParams';
import useGetPlaybackStopUavParams from 'services/uav/useGetPlaybackStopUavParams';
import useGetWatchUavParams, {
    GetWatchUavParamsProps,
} from 'services/uav/useGetWatchUavParams';
import { logUavAnalytics } from 'services/uav/utils';

import { isEmpty } from 'utils/object';
import { isRecordingItem } from 'utils/recordings';

import { PlayerError } from 'screens/PlayerModal/types';

type Params = {
    templateId: string | undefined;
    railId: string | undefined;
    videoItem: VideoItem | undefined;
    playbackType: StreamTypes | undefined;
    playerProgress: number;
    playerError: PlayerError;
    videoDuration: number;
};

function useHandlePlaybackStartStopWatchEvents({
    templateId,
    railId,
    videoItem,
    playbackType,
    playerProgress,
    playerError,
    videoDuration,
}: Params) {
    const prevVideoItem = useRef<VideoItem | undefined>(videoItem);
    const prevPlayerProgress = useRef<number>(playerProgress);

    const [videoLoaded, setVideoLoaded] = useState(false);
    const [firstWatchEventTime, setFirstWatchEventTime] = useState<
        number | undefined
    >(undefined);
    const [secondWatchEventTime, setSecondWatchEventTime] = useState<
        number | undefined
    >(undefined);

    const getBaseUavParams = useGetBaseUavParams();
    const { getPlaybackStopUavParams } = useGetPlaybackStopUavParams({
        playbackType,
        videoItem,
    });
    const { getPlaybackStartUavParams } = useGetPlaybackStartUavParams({
        playbackType,
        videoItem,
    });
    const { getWatchUavParams } = useGetWatchUavParams(playbackType);

    useEffect(() => {
        if (videoLoaded) {
            setFirstWatchEventTime(
                Math.floor(playerProgress + (videoDuration * 15) / 100),
            );
            setSecondWatchEventTime(
                Math.floor(playerProgress + (videoDuration * 50) / 100),
            );
        } else {
            setFirstWatchEventTime(undefined);
            setSecondWatchEventTime(undefined);
        }
    }, [videoDuration, videoLoaded]);

    const handleVideoLoaded = useCallback(() => {
        setVideoLoaded(true);
    }, []);

    useEffect(() => {
        if (playerProgress) {
            prevPlayerProgress.current = playerProgress;
        }
    }, [playerProgress]);

    useEffect(() => {
        if (videoLoaded) {
            logUavAnalytics(
                getPlaybackStartUavParams({
                    templateId,
                    railId,
                }),
            );
        }
    }, [videoLoaded]);

    useEffect(() => {
        if (
            prevVideoItem.current &&
            videoItem &&
            prevVideoItem.current.id !== videoItem.id &&
            prevVideoItem.current.channelId === videoItem.channelId
        ) {
            // Send `playbackStop` for currently finished live event and
            // `playbackStart` for just started new live event.
            logUavAnalytics(
                getPlaybackStopUavParams({
                    position: prevPlayerProgress.current || 0,
                    editorialID: prevVideoItem.current.id,
                    ...getBaseUavParams(UavEvents.PLAYBACK_STOP)?.metadata,
                }),
            );
            logUavAnalytics(
                getPlaybackStartUavParams({
                    templateId,
                    railId,
                }),
            );
        } else if (prevVideoItem.current?.id !== videoItem?.id) {
            // Send `playbackStop` UAV event if user closed playback(player)
            // and if playback stop was not caused by playerError.
            if (prevVideoItem.current?.id && isEmpty(playerError)) {
                const params = getPlaybackStopUavParams({
                    position: prevPlayerProgress.current || 0,
                    editorialID: prevVideoItem.current.id,
                    ...getBaseUavParams(UavEvents.PLAYBACK_STOP)?.metadata,
                });

                if (params) {
                    logUavAnalytics(params);
                }
            }

            setVideoLoaded(false);
        }

        prevVideoItem.current = videoItem;
    }, [videoItem]);

    // Send `playbackStop` UAV event as soon as playerError appeared.
    useEffect(() => {
        if (!isEmpty(playerError) && videoItem) {
            const params = getPlaybackStopUavParams({
                position: playerProgress || 0,
                editorialID: videoItem.id,
                ...getBaseUavParams(UavEvents.PLAYBACK_STOP)?.metadata,
            });

            if (params) {
                logUavAnalytics(params);
            }
        }
    }, [playerError]);

    // Send `watch` UAV event.
    useEffect(() => {
        if (
            templateId &&
            videoItem &&
            firstWatchEventTime != null &&
            secondWatchEventTime != null &&
            (playerProgress === firstWatchEventTime ||
                playerProgress === secondWatchEventTime)
        ) {
            const params: GetWatchUavParamsProps = {
                templateId,
                railId,
                contentId: videoItem.id,
            };

            if (isRecordingItem(videoItem.originalProgram)) {
                params.recordingId = videoItem.originalProgram.id;
            }

            logUavAnalytics(getWatchUavParams(params, videoItem));
        }
    }, [
        firstWatchEventTime,
        getWatchUavParams,
        playerProgress,
        railId,
        secondWatchEventTime,
        templateId,
        videoItem,
    ]);

    return {
        onVideoLoaded: handleVideoLoaded,
    };
}

export default useHandlePlaybackStartStopWatchEvents;
